import type { Language } from '@eformulieren/engine'
import type { ProjectionSection, Text } from '@eformulieren/model'
import { type ReactNode, type Ref, useId } from 'react'
import { WarningIcon } from '../icons/WarningIcon.js'
import { inLanguage } from '../language/text.js'

/**
 * Het overzicht van wat er nog openstaat (R-24, R-140, R-139 met WCAG 3.3.1 en 3.3.3, R-81, R-152).
 *
 * **Waarom het er is.** Indienen kan pas als elke harde controle voldoet (R-31, R-81), en een
 * melding verschijnt bij een veld pas als de invuller het heeft verlaten (R-25). Wie een leeg
 * formulier van twintig secties krijgt en een uitgeschakelde Indienen ziet, weet dus niet welke
 * velden het zijn, en een melding die hij niet kan vinden is niet "met het toetsenbord
 * bereikbaar" (R-140). R-24 vraagt dat de invuller tijdens het invullen ziet welke voorwaarden nog
 * niet gehaald zijn; dit is die plek.
 *
 * **Eén regel per voorwaarde die de beurt nog tegenhoudt, en niets anders**: per openstaande harde
 * controle, en sinds het besluit van 9 oktober 2026 per criterium van de eigen laag zonder
 * menselijk oordeel (R-152, R-180), want ook dat houdt indienen en de toetsknoppen tegen. Het
 * aantal regels is het getal in de voet, zodat twee plekken op hetzelfde scherm elkaar niet
 * tegenspreken; de inhoudsopgave telt alleen de harde controles en zegt dat ook. Een waarschuwing
 * staat er niet in: die blokkeert niets (R-23) en heeft haar eigen plek bij het veld. Een regel op
 * een rijveld is één controle, hoeveel rijen er ook falen; de regel noemt dan alle rijen en de
 * link gaat naar de eerste. Een regel voor een criterium gaat naar het criterium zelf.
 *
 * **Elke regel is een link naar een element met een id**, de schil om het veld. Een link en geen
 * knop, want het is een sprong naar een plek; de aanroeper vangt de klik af om eerst de sectie
 * open te klappen en daarna de focus te zetten (`focusInField`), want in een dichte sectie kan
 * niets de focus krijgen. Zonder script werkt het anker nog steeds als gewone sprong.
 *
 * **Een regio met een kop, en geen live region.** De kop krijgt de focus nadat de invuller om
 * het overzicht vroeg, en een schermlezer leest dan de naam van de regio en het aantal voor.
 * `role="alert"` of `aria-live` zou bij elke aanslag opnieuw gaan praten, want het overzicht volgt
 * de stand live; de statusregio in de inhoudsopgave meldt het aantal al.
 *
 * **Dicht te doen, en open bij een sprong** (live test van 3 november 2026). Eerst ging het overzicht
 * niet meer dicht. Nu heeft het rechtsboven Sluiten, en Escape sluit het zolang de focus erin staat;
 * de aanroeper zet de focus daarna terug op wat het opende. Een punt kiezen laat het open, zodat de
 * invuller na het ene veld het volgende kan kiezen.
 *
 * **De vorm komt uit de schermbasis en niet uit de CitatenPOC**, want die heeft geen
 * foutsamenvatting: daar draagt de inhoudsopgave per vraag een markering. Dit overzicht volgt het
 * bekende patroon van een foutsamenvatting bovenaan het formulier, in de kaartopmaak en de
 * foutkleuren van de meldingen bij een veld (`.ef-veld__melding--hard`).
 */

export interface OutstandingEntry {
  /** Uniek binnen het overzicht: het regel-id, want één regel is één openstaande controle. */
  readonly sleutel: string
  readonly sectieId: string
  /** Het id van het element waar de link naartoe wijst (`FieldShell` met `anker`, of de sectie). */
  readonly anker: string
  /** Waar het over gaat, al in de taal van de invuller: het veld, of groep, rij en veld. */
  readonly naam: string
  readonly melding: Text
}

export interface OutstandingOverviewProps {
  readonly secties: readonly ProjectionSection[]
  readonly regels: readonly OutstandingEntry[]
  readonly taal: Language
  readonly headerRef: Ref<HTMLHeadingElement>
  readonly onJump: (rule: OutstandingEntry) => void
  /**
   * Of de lezer zelf indient. Een medeschrijver doet dat niet (R-69): dan gaat het overzicht over zijn
   * secties en zegt het niets over indienen (audit delen G-03, mockup 07).
   */
  readonly indienen?: boolean
  /**
   * De foutvorm: rode rand, rode kop en het waarschuwingsteken. Alleen nadat de lezer koos voor
   * "Controleren en indienen" (besluit van 2 november 2026); vanuit de voortgang in de kop opent hetzelfde
   * overzicht neutraal, want een leeg verplicht veld in een concept is geen fout. Standaard aan.
   */
  readonly fout?: boolean
  /** Het id van de regio, voor `aria-controls` op de knop die het overzicht open- en dichtdoet. */
  readonly id?: string
  /** Sluiten, met de knop of Escape; zonder deze prop staat er geen knop. */
  readonly onSluiten?: () => void
}

const CLOSE: Text = { nl: 'Sluiten', en: 'Close' }

const HEADER = (count: number): Text => ({
  nl: count === 1 ? 'Nog 1 punt voordat u kunt indienen' : `Nog ${String(count)} punten voordat u kunt indienen`,
  en: count === 1 ? '1 item left before you can submit' : `${String(count)} items left before you can submit`,
})

const DONE: Text = { nl: 'Alles is ingevuld', en: 'Everything is filled in' }

const OWN_HEADER = (count: number): Text => ({
  nl: count === 1 ? 'Nog 1 punt in uw secties' : `Nog ${String(count)} punten in uw secties`,
  en: count === 1 ? '1 item left in your sections' : `${String(count)} items left in your sections`,
})
const OWN_DONE: Text = { nl: 'Alles in uw secties is ingevuld', en: 'Everything in your sections is filled in' }
const OWN_DONE_EXPLANATION: Text = { nl: 'Indienen doet de aanvrager.', en: 'The applicant submits.' }

const EXPLANATION: Text = {
  nl: 'Kies een punt om naar het veld te gaan.',
  en: 'Choose an item to go to the field.',
}

const DONE_EXPLANATION: Text = {
  nl: 'Er staat niets meer open. U kunt indienen.',
  en: 'Nothing is open any more. You can submit.',
}

export function OutstandingOverview({
  secties: sections,
  regels: rules,
  taal: language,
  headerRef,
  onJump,
  indienen: submits = true,
  fout: asError = true,
  id,
  onSluiten: onClose,
}: OutstandingOverviewProps): ReactNode {
  const headerId = useId()
  const empty = rules.length === 0
  return (
    <section
      className="ef-openstaand"
      id={id}
      aria-labelledby={headerId}
      data-leeg={empty ? 'true' : undefined}
      data-toon={asError ? 'fout' : 'neutraal'}
      onKeyDown={
        onClose === undefined
          ? undefined
          : (event) => {
              if (event.key !== 'Escape') return
              event.preventDefault()
              onClose()
            }
      }
    >
      <div className="ef-openstaand__boven">
        <h2 className="ef-openstaand__kop" id={headerId} ref={headerRef} tabIndex={-1}>
          {empty || !asError ? null : <WarningIcon aria-hidden="true" />}
          {inLanguage(submits ? (empty ? DONE : HEADER(rules.length)) : empty ? OWN_DONE : OWN_HEADER(rules.length), language)}
        </h2>
        {onClose === undefined ? null : (
          <button type="button" className="ef-openstaand__sluit" onClick={onClose}>
            {inLanguage(CLOSE, language)}
          </button>
        )}
      </div>
      <p className="ef-openstaand__uitleg">{inLanguage(empty ? (submits ? DONE_EXPLANATION : OWN_DONE_EXPLANATION) : EXPLANATION, language)}</p>
      {sections.map((section) => {
        const own = rules.filter((rule) => rule.sectieId === section.id)
        if (own.length === 0) return null
        return (
          <div className="ef-openstaand__sectie" key={section.id}>
            <h3 className="ef-openstaand__sectiekop">{inLanguage(section.titel, language)}</h3>
            <ul className="ef-openstaand__lijst">
              {own.map((rule) => (
                <li key={rule.sleutel}>
                  <a
                    className="ef-openstaand__link"
                    href={`#${rule.anker}`}
                    onClick={(event) => {
                      event.preventDefault()
                      onJump(rule)
                    }}
                  >
                    <span className="ef-openstaand__naam">{rule.naam}</span>
                    {': '}
                    {inLanguage(rule.melding, language)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </section>
  )
}
